import React from 'react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import useProjectBreadcrumb from 'src/shared/hooks/useProjectBreadcrumb';
import { ProjectViews } from 'src/routes/routesConfig';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import BigCatImage from 'src/assets/images/maps/species-map/freemium/big-cat.webp';
import EdnaSyringeImage from 'src/assets/images/maps/species-map/freemium/edna-syringe.webp';
import EdnaLabImage from 'src/assets/images/maps/species-map/freemium/edna-lab.webp';

import MapScreenImage from 'src/assets/images/maps/species-map/freemium/map-screen.webp';
import DashboardImage from 'src/assets/images/maps/species-map/freemium/dashboard.webp';
import CommunityComparisonImage from 'src/assets/images/maps/species-map/freemium/community-comparison.webp';
import FreemiumCard from 'src/shared/templates/freemium-card/FreemiumCard';

const FreemiumSpeciesInsights = () => {
    const currentProjectBreadcrumbDetails = useProjectBreadcrumb('Species insights');

    const handleFindOutMoreClick = () => {
        window.open('https://www.naturemetrics.com/species-detection', '_blank');
    };

    const handleBookCallClick = () => {
        window.open('https://www.naturemetrics.com/book-a-demo', '_blank');
    };

    const mainContent = (
        <div className='inline-flex flex-col items-start gap-8 mb-8'>
            <div className='p-5 bg-white rounded-xl flex flex-col  items-start gap-5 w-full shadow-lg'>
                <div className='rounded-xl flex flex-col xl:flex-row lg:[&:not(:has(.xl\:flex-row))]:flex-row gap-4'>
                    <img className='w-[440px] h-60 rounded-xl' src={BigCatImage} />
                    <div className='flex-1 flex flex-col items-start gap-5'>
                        <div className='w-full max-w-[600px] text-2xl font-bold'>
                            Unlock Deeper Biodiversity Intelligence with Species Insights
                        </div>
                        <div className='w-full max-w-[600px] text-grey-80 text-sm font-normal leading-tight'>
                            Discover the full story of your site&apos;s biodiversity health. Our subscriptions give you powerful,
                            data-driven insights into the species and ecosystems that matter most — powered by cutting-edge eDNA technology.
                            <br />
                            <br />
                            Track biodiversity trends over time, access automated metrics and actionable reports, benchmark ecological
                            health across sites and meet compliance and sustainability goals with confidence.
                        </div>
                        <div className='inline-flex  items-start gap-3.5'>
                            <PrimaryButton onClick={handleBookCallClick}>Book a call</PrimaryButton>
                        </div>
                    </div>
                </div>

                <div className='self-stretch h-px relative'>
                    <div className='w-full h-px left-0 top-0 absolute bg-grey-20' />
                </div>

                <div className='rounded-xl flex flex-col xl:flex-row lg:[&:not(:has(.xl\:flex-row))]:flex-row  items-start gap-5'>
                    <div className='flex  items-start gap-3.5'>
                        <img className='w-53 h-46 rounded-xl' src={EdnaSyringeImage} />
                        <img className='w-53 h-46 rounded-xl' src={EdnaLabImage} />
                    </div>
                    <div className='flex-1 inline-flex flex-col  items-start gap-4'>
                        <div className='w-full max-w-[600px] text-2xl font-bold'>New to eDNA?</div>
                        <div className='w-full max-w-[600px] text-grey-80 text-sm font-normal leading-tight'>
                            Discover thousands of hard-to-detect species in a fraction of the time. eDNA provides an unrivalled picture of a
                            site&apos;s biodiversity, offering the most comprehensive and scalable solution for biodiversity monitoring.
                            <br />
                            <br />
                            Find out more about high-integrity, site-based biodiversity surveys from the world&apos;s largest commercial
                            eDNA company.
                        </div>
                        <SecondaryButton onClick={handleFindOutMoreClick}>Read more about eDNA</SecondaryButton>
                    </div>
                </div>
            </div>

            <div className='flex flex-col items-start gap-4'>
                <div className=' text-primary text-2xl font-normal'>
                    Visualise your species insights right here, on the NatureMetrics Intelligence Platform{' '}
                </div>
                <div className=' text-neutral-800 text-xl font-normal'>
                    Included in a silver or gold level subscription with NatureMetrics:
                </div>
            </div>

            <div className='flex flex-wrap gap-6'>
                <FreemiumCard
                    title='Species map'
                    description='Easily navigate your data and add geographical context with an interactive map of survey sites'
                    image={<img src={MapScreenImage} alt='Metrics' />}
                    footer={
                        <div className='text-grey-80'>
                            <div className='mb-3'>Includes:</div>
                            <ul className='list-disc list-outside ms-6 '>
                                <li>
                                    <b>Locations:</b> All sampling coordinates plotted on the interactive map
                                </li>
                                <li>
                                    <b>Sample overview:</b> List of named species detected at each location
                                </li>
                                <li>
                                    <b>Spatial Metrics:</b> View metrics plotted spatially
                                </li>
                                <li>
                                    <b>Species focus:</b> Discover where each species was detected
                                </li>
                                <li>
                                    <b>Filtering:</b> Interrogate the data by filtering by time, test, sampling group and IUCN or invasive
                                    categorisations.
                                </li>
                            </ul>
                        </div>
                    }
                />

                <FreemiumCard
                    title='Nature Performance Dashboard'
                    description='Access deeper levels of data analysis. Gain actionable insights with an automated suite of insights'
                    image={<img src={DashboardImage} alt='Metrics' />}
                    footer={
                        <div className='text-grey-80'>
                            <div className='mb-3'>Includes:</div>
                            <ul className='list-disc list-outside ms-6 '>
                                <li>
                                    <b>Metrics and trends:</b> Key metrics displayed as a time series
                                </li>
                                <li>
                                    <b>Species of interest:</b> All IUCN Red List, invasive and CHEGD named species detected.
                                </li>
                                <li>
                                    <b>Species highlight:</b> Information about a notable species detected in your project
                                </li>
                            </ul>
                        </div>
                    }
                />

                <FreemiumCard
                    title='Ecology Hub - Ecology data visualisations'
                    description='Save time data wrangling. Easily compare different sites and groups from across your portfolio'
                    image={<img src={CommunityComparisonImage} alt='Metrics' />}
                    footer={
                        <div className='text-grey-80'>
                            <div className='mb-3'>Includes:</div>
                            <ul className='list-disc list-outside ms-6 '>
                                <li>
                                    <b>Species Trends:</b> Time series by sample group
                                </li>
                                <li>
                                    <b>Sample Comparison:</b> Compare metric values across samples
                                </li>
                                <li>
                                    <b>Statistical Comparison:</b> Compare the spread and statistical significance between two variables
                                </li>
                                <li>
                                    <b>Community Comparison:</b> Compare and track community composition across sample groups
                                </li>
                                <li>
                                    <b>Taxonomic Composition:</b> A heat tree chart showing species observations across each taxonomic rank
                                </li>
                            </ul>
                        </div>
                    }
                />
            </div>
        </div>
    );

    return (
        <StandardAppLayout
            mainContent={mainContent}
            breadcrumbs={currentProjectBreadcrumbDetails}
            title='Species insights'
            page={ProjectViews.FREEMIUM_SPECIES_INSIGHTS}
        />
    );
};

export default FreemiumSpeciesInsights;
